<template>
    <div
        class="ds-textarea"
        :class="{
            'is-disabled': disabled || readonly,
            'has-error': error,
            'is-active': active,
            'has-value': !!modelValue,
        }"
    >
        <textarea
            :id="id"
            v-model="message"
            :class="{ 'resize-none': !resizable }"
            :name="$attrs.name || $attrs.id || null"
            :placeholder="placeholder"
            :aria-placeholder="label"
            :aria-multiline="$attrs['aria-multiline'] || true"
            :aria-readonly="readonly"
            :aria-disabled="disabled"
            :role="$attrs.role || 'textbox'"
            :maxlength="$attrs.maxlength || 1000"
            :rows="$attrs.rows || 3"
            :readonly="readonly"
            :required="!!$attrs.required"
            :disabled="disabled"
            @focus="isFocused = true"
            @blur="isFocused = false"
        />
        <label class="textarea-label" :for="id">{{ label }}</label>
        <Transition name="fade">
            <div v-if="iconAfter && !resetBtn" class="after">
                <slot name="after">
                    <component
                        :is="onClickAfter ? 'button' : 'div'"
                        v-if="iconAfter"
                        :type="onClickAfter ? 'button' : null"
                        class="icon-button"
                        @click="onClickAfter"
                    >
                        <DSFontIcon
                            :code="trailingIcon?.code"
                            :size="trailingIcon?.size"
                            :icon-style="trailingIcon?.iconStyle"
                        />
                    </component>
                </slot>
            </div>
            <button v-else-if="resetBtn && modelValue" class="after" @click="message = ''">
                <DSFontIcon
                    :code="trailingIcon?.code || 'close'"
                    :size="trailingIcon?.size || 'l'"
                    :icon-style="trailingIcon?.iconStyle || 'line'"
                />
            </button>
        </Transition>
        <div
            v-if="error || helpText"
            :id="errorHelperId"
            class="text-after"
            :class="{
                'help-text': helpText,
                'error': error,
            }"
        >
            {{ error ? error : helpText }}
        </div>
    </div>
</template>

<script setup>
import { ref, computed, useNuxtApp } from '#imports';

const { $dsc } = useNuxtApp();

const props = defineProps({
    id: {
        type: String,
        default: '',
    },
    modelValue: {
        type: String,
        default: '',
    },
    label: {
        type: String,
        default: '',
    },
    placeholder: {
        type: String,
        default: '',
    },
    resizable: {
        type: Boolean,
        default: false,
    },
    helpText: {
        type: String,
        default: null,
    },
    error: {
        type: String,
        default: null,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    iconAfter: {
        type: [Boolean, String, Object],
        default: false,
    },
    onClickAfter: {
        type: Function,
        default: null,
    },
    resetBtn: {
        type: Boolean,
        default: false,
    },
});

const isFocused = ref();

const emit = defineEmits(['update:modelValue']);

const message = computed({
    get: () => props.modelValue,
    set: value => emit('update:modelValue', value),
});

const getIcon = ({ icon }) => {
    if (!icon) return false;

    if (typeof icon === 'string') return { code: icon || icon.code, size: icon.size || 'm' };

    return icon;
};

const trailingIcon = computed(() => getIcon({ icon: props.iconAfter, leading: false }));
const active = computed(() => !!props.modelValue || isFocused);
const errorHelperId = computed(() => props.id ? `error-helper-${props.id}` : $dsc.uuidv4());
</script>
